import { formatNumber } from 'libphonenumber-js'
import PropTypes from 'prop-types'
import React, { Fragment } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { translate } from 'react-i18next'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { compose } from 'recompose'
import * as yup from 'yup'
import { getTac, registerAccount } from '../actions/AuthAction'
import { Button, ErrorText, Form, Label, List, Modal, ModalBody, PasswordField, Stack } from '../components'
import { yupToFormErrors } from '../components/Validator'
import * as routeService from '../services/routeService'
import * as translationService from '../services/translationService'
import { ERROR_PASSWORD_REGEX, REQUIRED_FIELD } from './../utils/validation/constants'
import AuthContent from './AuthContent'
import TacModal from './TacModal'

const mapStateToProps = ({ AuthReducer, FlowReducer }) => {
  const { user = {}, error, tac } = AuthReducer
  const { phaseInformationError, applicationProfileError, property } = FlowReducer
  const { mobileNumber, email } = user
  return {
    tac,
    mobileNumber,
    email,
    error,
    phaseInformationError,
    applicationProfileError,
    property,
  }
}

const mapDispatchToProps = dispatch => ({
  getTacRequest: () => dispatch(getTac()),
  registerAccount: (mobileNumber, email, password, captcha, tacId) =>
    dispatch(registerAccount(mobileNumber, email, password, captcha, tacId)),
})

class RegisterPasswordView extends React.Component {
  constructor(props) {
    super(props)

    this.captchaRef = React.createRef()
  }

  static propTypes = {
    mobileNumber: PropTypes.string,
    email: PropTypes.string,
    error: PropTypes.any,
    registerAccount: PropTypes.func,
    property: PropTypes.any,
    tac: PropTypes.any,
    phaseInformationError: PropTypes.any,
    applicationProfileError: PropTypes.any,
    getTacRequest: PropTypes.any,
  }

  validation = yup.object({
    password: yup
      .string()
      .required(REQUIRED_FIELD)
      .min(8, ERROR_PASSWORD_REGEX),

    captcha: yup.string().required(REQUIRED_FIELD),
  })

  state = {
    password: '',
    captcha: undefined,
    inputValidation: {},
    tacModalisOpen: false,
    registerInfoModalIsOpen: false,
  }

  onValueChange = event => {
    const { id, value } = event.target
    this.setState({
      [id]: value,
    })
  }

  resetErrors = () => {
    this.setState({
      inputValidation: {},
    })
  }

  doRegister = async event => {
    event.preventDefault()

    if (this.captchaRef.current) {
      this.captchaRef.current.reset()
    }

    this.resetErrors()

    const { mobileNumber, email, tac: { id = 1 } = {} } = this.props

    try {
      await this.validation.validate(this.state, { abortEarly: false })
      await this.props.registerAccount(mobileNumber, email, this.state.password, this.state.captcha, id)
    } catch (err) {
      const validationErrors = yupToFormErrors(err)
      this.setState({
        inputValidation: validationErrors,
      })
    }
  }

  toggleModal = () => {
    this.setState(prevState => {
      return { tacModalisOpen: !prevState.tacModalisOpen }
    })
  }

  toggleRegisterInfoModal = () => {
    this.setState(prevState => {
      return {
        registerInfoModalIsOpen: !prevState.registerInfoModalIsOpen,
      }
    })
  }

  componentDidMount() {
    this.props.getTacRequest()
    if (!this.props.mobileNumber) {
      routeService.goToLoginPage()
    }
  }

  render() {
    const { error, phaseInformationError, applicationProfileError, mobileNumber } = this.props
    const { inputValidation } = this.state

    let errorMessage = undefined
    if (error) {
      try {
        if (error.message) {
          errorMessage = error.message
        } else {
          errorMessage = translationService.loginPasswordFailedAuthentication()
        }
      } catch (x) {
        errorMessage = translationService.loginPasswordFailedAuthentication()
      }
    }

    return (
      <Fragment>
        <AuthContent
          title={translationService.registerTitle()}
          // subtitle={translationService.system('register.message')}
          hasToken={false}
          withSpacer={false}>
          <Form onSubmit={this.onValueChange}>
            {(phaseInformationError || applicationProfileError) && (
              <ErrorText>{translationService.generalError()}</ErrorText>
            )}

            {mobileNumber && (
              <div>
                <Label>{translationService.loginMobileNumberPlaceholder()}</Label>

                <Stack distribution="EqualSpacing">
                  <Stack.Item>
                    <strong>{formatNumber(mobileNumber, 'International')}</strong>
                  </Stack.Item>
                  <Stack.Item>
                    <Link to={routeService.getPrefixPath() + '/login'}>{translationService.system('edit')}</Link>
                  </Stack.Item>
                </Stack>

                {/* <div className={labeledStyles.HelpText}>{translationService.authSignUpMobileNumberHelpText()}</div> */}
              </div>
            )}

            <PasswordField
              label={translationService.system('register.message')}
              id="password"
              autoFocus
              value={this.state.password}
              onChange={this.onValueChange}
              error={inputValidation.password || errorMessage}
            />

            <ReCAPTCHA
              id="captcha"
              ref={this.captchaRef}
              sitekey={process.env.REACT_APP_CAPTCHA_KEY}
              onChange={value => {
                this.onValueChange({ target: { id: 'captcha', value } })
              }}
            />

            <Button primary submit onClick={this.doRegister}>
              {translationService.system('next')}
            </Button>

            <p>
              {translationService.registerTacAccept()}{' '}
              <a href="#" onClick={this.toggleModal}>
                {translationService.registerTacAcceptLink()}
              </a>
              .{' '}
            </p>

            <div>
              <Button plain onClick={this.toggleRegisterInfoModal}>
                {translationService.registerWhyTitle()}
              </Button>
            </div>
          </Form>
        </AuthContent>

        <TacModal isOpen={this.state.tacModalisOpen} onRequestClose={this.toggleModal} tac={this.state.tac} />

        <Modal
          isOpen={this.state.registerInfoModalIsOpen}
          onRequestClose={this.toggleRegisterInfoModal}
          title={translationService.registerWhyTitle()}>
          <ModalBody>
            <List type="checkmark">
              <List.Item>{translationService.registerWhyItem1()}</List.Item>
              <List.Item>{translationService.registerWhyItem2()}</List.Item>
              <List.Item>{translationService.registerWhyItem3()}</List.Item>
            </List>
          </ModalBody>
        </Modal>
      </Fragment>
    )
  }
}

export default compose(
  translate('translations'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(RegisterPasswordView)



// WEBPACK FOOTER //
// ./src/views/RegisterPasswordView.js